import { ThumbsUp, MessageSquare, Share2, Eye, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export default function PerformanceRecordDialog({
  open,
  onOpenChange,
  darkMode,
  selectedContent,
  performanceForm,
  setPerformanceForm,
  recordPerformance,
  recording,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
        <DialogHeader>
          <DialogTitle className={darkMode ? "text-white" : ""}>Record Performance</DialogTitle>
          <DialogDescription className={darkMode ? "text-gray-400" : ""}>
            Enter the engagement metrics for this content post.
          </DialogDescription>
        </DialogHeader>

        {selectedContent && (
          <div className="space-y-4">
            <div className={`p-3 rounded-lg ${darkMode ? "bg-[#21262d]" : "bg-gray-50"}`}>
              <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                {selectedContent.topic}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={darkMode ? "text-gray-300" : ""}>Platform</Label>
                <Select
                  value={performanceForm.platform}
                  onValueChange={(value) =>
                    setPerformanceForm({ ...performanceForm, platform: value })
                  }
                >
                  <SelectTrigger className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="twitter">Twitter/X</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={darkMode ? "text-gray-300" : ""}>Hook Used</Label>
                <Select
                  value={performanceForm.hook_used}
                  onValueChange={(value) =>
                    setPerformanceForm({ ...performanceForm, hook_used: value })
                  }
                >
                  <SelectTrigger className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}>
                    <SelectValue placeholder="Select hook" />
                  </SelectTrigger>
                  <SelectContent className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
                    <SelectItem value="curiosity">Curiosity</SelectItem>
                    <SelectItem value="contrarian">Contrarian</SelectItem>
                    <SelectItem value="fomo">Fear of Loss (FOMO)</SelectItem>
                    <SelectItem value="authority">Authority</SelectItem>
                    <SelectItem value="practical">Practical Benefit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${darkMode ? "text-gray-300" : ""}`}>
                  <ThumbsUp className="w-4 h-4" /> Likes
                </Label>
                <Input
                  type="number"
                  value={performanceForm.likes}
                  onChange={(e) =>
                    setPerformanceForm({ ...performanceForm, likes: parseInt(e.target.value) || 0 })
                  }
                  className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${darkMode ? "text-gray-300" : ""}`}>
                  <MessageSquare className="w-4 h-4" /> Comments
                </Label>
                <Input
                  type="number"
                  value={performanceForm.comments}
                  onChange={(e) =>
                    setPerformanceForm({
                      ...performanceForm,
                      comments: parseInt(e.target.value) || 0,
                    })
                  }
                  className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${darkMode ? "text-gray-300" : ""}`}>
                  <Share2 className="w-4 h-4" /> Shares
                </Label>
                <Input
                  type="number"
                  value={performanceForm.shares}
                  onChange={(e) =>
                    setPerformanceForm({
                      ...performanceForm,
                      shares: parseInt(e.target.value) || 0,
                    })
                  }
                  className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${darkMode ? "text-gray-300" : ""}`}>
                  <Eye className="w-4 h-4" /> Impressions
                </Label>
                <Input
                  type="number"
                  value={performanceForm.impressions}
                  onChange={(e) =>
                    setPerformanceForm({
                      ...performanceForm,
                      impressions: parseInt(e.target.value) || 0,
                    })
                  }
                  className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className={darkMode ? "text-gray-300" : ""}>Notes (optional)</Label>
              <Input
                value={performanceForm.notes}
                onChange={(e) =>
                  setPerformanceForm({ ...performanceForm, notes: e.target.value })
                }
                placeholder="Any observations about performance..."
                className={darkMode ? "bg-[#21262d] border-[#30363d] text-white" : ""}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={recordPerformance}
            disabled={recording}
            className="bg-[#DC143C] hover:bg-[#b91031] text-white"
          >
            {recording ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Recording...
              </>
            ) : (
              "Save Performance"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
